// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "seen by" read receipts for DMs, group DMs and server text channels.
// Populated by the MESSAGE_READ_RECEIPT gateway event and hydrated per channel
// over REST, or many chats at once for the chat list's ticks. The store also
// answers where each reader's avatar belongs — see seenAnchors.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {buildSeenAnchors, type SeenAnchorMode} from '@app/features/read_state/utils/SeenAnchors';
import {ChannelTypes, GUILD_TEXT_BASED_CHANNEL_TYPES} from '@konvo/constants/src/ChannelConstants';
import {computed, type IComputedValue, makeAutoObservable} from 'mobx';

/** Channels the server keeps receipts for — mirrors isReceiptChannelType in the API. */
export function isReadReceiptChannelType(type: number): boolean {
	return type === ChannelTypes.DM || type === ChannelTypes.GROUP_DM || GUILD_TEXT_BASED_CHANNEL_TYPES.has(type);
}

/**
 * A DM marks your own messages with who read them; groups and server channels
 * mark where everyone has read up to, whoever wrote the message.
 */
export function seenAnchorModeFor(type: number): SeenAnchorMode {
	return type === ChannelTypes.DM ? 'own' : 'all';
}

interface ReadReceiptEvent {
	channelId: string;
	userId: string;
	messageId: string;
	/** ISO time of the reader's last ack; absent from older servers. */
	readAt?: string;
}

interface ReadReceiptEntryResponse {
	user_id: string;
	message_id: string;
	read_at: string;
}

interface ReadReceiptsResponse {
	receipts: Array<ReadReceiptEntryResponse>;
}

interface PrivateChannelReceiptsResponse {
	channels: Record<string, Array<ReadReceiptEntryResponse>>;
}

/** The batch endpoint takes at most this many chats per call. */
const MAX_RECEIPT_BATCH = 100;
/** Long enough for a list render to settle into one request. */
const RECEIPT_BATCH_DELAY_MS = 50;

function isNewer(candidate: string, existing: string | undefined): boolean {
	if (existing == null) {
		return true;
	}
	try {
		return BigInt(candidate) > BigInt(existing);
	} catch {
		return false;
	}
}

class ReadReceiptsStore {
	/** channelId -> (userId -> last read messageId) */
	receiptsByChannel = new Map<string, Map<string, string>>();
	/** channelId:userId -> when that reader last moved their mark, epoch ms. */
	readAtByReader = new Map<string, number>();
	/** Channels whose receipts came from the server, so a reader missing from them has read nothing. */
	loadedChannels = new Set<string>();
	private fetchedChannels = new Set<string>();
	private pendingBatch = new Set<string>();
	private batchHandle: ReturnType<typeof setTimeout> | null = null;
	/** channelId:userId:mode -> anchors, so every message row costs one map lookup. */
	private anchorsByChannel = new Map<string, IComputedValue<Map<string, Array<string>>>>();

	constructor() {
		makeAutoObservable<this, 'fetchedChannels' | 'anchorsByChannel' | 'pendingBatch' | 'batchHandle'>(
			this,
			{fetchedChannels: false, anchorsByChannel: false, pendingBatch: false, batchHandle: false},
			{autoBind: true},
		);
	}

	/**
	 * Message id -> the readers whose avatar belongs on it, for the current
	 * user's own messages in this channel. Cached per channel as a computed, so
	 * the scan re-runs only when the receipts or the loaded messages change —
	 * and only while an observer is watching it.
	 */
	seenAnchors(
		channelId: string,
		currentUserId: string | null,
		mode: SeenAnchorMode = 'own',
	): ReadonlyMap<string, ReadonlyArray<string>> {
		const key = `${channelId}:${currentUserId ?? ''}:${mode}`;
		let anchors = this.anchorsByChannel.get(key);
		if (!anchors) {
			anchors = computed(() => {
				// ChannelMessages is a plain value object; the store's counter is what
				// makes a new message invalidate this.
				void Messages.version;
				const messages = Messages.getMessages(channelId);
				return buildSeenAnchors(messages.toArray(), this.receiptsByChannel.get(channelId), currentUserId, {
					mode,
					hasMoreAfter: messages.hasMoreAfter,
				});
			});
			this.anchorsByChannel.set(key, anchors);
		}
		return anchors.get();
	}

	getChannelReceipts(channelId: string): ReadonlyMap<string, string> | undefined {
		return this.receiptsByChannel.get(channelId);
	}

	/** When the reader last moved their mark in this channel, or null if unknown. */
	getReadAt(channelId: string, userId: string): number | null {
		return this.readAtByReader.get(`${channelId}:${userId}`) ?? null;
	}

	/** Users (other than `excludeUserId`) whose last read message is >= messageId. */
	getSeenBy(channelId: string, messageId: string, excludeUserId?: string): Array<string> {
		const receipts = this.receiptsByChannel.get(channelId);
		if (!receipts) {
			return [];
		}
		const seen: Array<string> = [];
		for (const [userId, readMessageId] of receipts) {
			if (userId === excludeUserId) {
				continue;
			}
			try {
				if (BigInt(readMessageId) >= BigInt(messageId)) {
					seen.push(userId);
				}
			} catch {
				// ignore malformed ids
			}
		}
		return seen;
	}

	handleReceipt(params: ReadReceiptEvent): void {
		const {channelId, userId, messageId, readAt} = params;
		let receipts = this.receiptsByChannel.get(channelId);
		if (!receipts) {
			this.receiptsByChannel.set(channelId, new Map());
			// The store keeps its own observable copy of the map it is given; write to that one,
			// or the channel's first receipt (a DM's only one) is lost.
			receipts = this.receiptsByChannel.get(channelId)!;
		}
		if (isNewer(messageId, receipts.get(userId))) {
			receipts.set(userId, messageId);
			const readAtMs = readAt ? Date.parse(readAt) : Number.NaN;
			this.readAtByReader.set(`${channelId}:${userId}`, Number.isNaN(readAtMs) ? Date.now() : readAtMs);
		}
	}

	async fetchForChannel(channelId: string): Promise<void> {
		if (this.fetchedChannels.has(channelId)) {
			return;
		}
		this.fetchedChannels.add(channelId);
		try {
			const response = await http.get<ReadReceiptsResponse>(
				Endpoints.CHANNEL_READ_RECEIPTS(channelId),
				RETRY_RATE_LIMITS,
			);
			this.applyLoaded(channelId, response.body?.receipts ?? []);
		} catch {
			// allow a retry on the next open
			this.fetchedChannels.delete(channelId);
		}
	}

	/**
	 * Load a DM's or group DM's receipts along with the other chats asking in the
	 * same moment, so a chat list costs one request rather than one per row.
	 */
	ensureForPrivateChannel(channelId: string): void {
		if (this.fetchedChannels.has(channelId)) return;
		this.fetchedChannels.add(channelId);
		this.pendingBatch.add(channelId);
		if (this.batchHandle != null) return;
		this.batchHandle = setTimeout(() => {
			this.batchHandle = null;
			void this.flushBatch();
		}, RECEIPT_BATCH_DELAY_MS);
	}

	private async flushBatch(): Promise<void> {
		const channelIds = Array.from(this.pendingBatch);
		this.pendingBatch.clear();
		for (let start = 0; start < channelIds.length; start += MAX_RECEIPT_BATCH) {
			const batch = channelIds.slice(start, start + MAX_RECEIPT_BATCH);
			try {
				const response = await http.post<PrivateChannelReceiptsResponse>(Endpoints.USER_READ_RECEIPTS, {
					body: {channels: batch},
					...RETRY_RATE_LIMITS,
				});
				const channels = response.body?.channels ?? {};
				for (const channelId of batch) {
					const entries = channels[channelId];
					if (entries) this.applyLoaded(channelId, entries);
					else this.fetchedChannels.delete(channelId);
				}
			} catch {
				// The rows keep "You: " and ask again when they next render.
				for (const channelId of batch) this.fetchedChannels.delete(channelId);
			}
		}
	}

	private applyLoaded(channelId: string, entries: ReadonlyArray<ReadReceiptEntryResponse>): void {
		for (const entry of entries) {
			this.handleReceipt({
				channelId,
				userId: entry.user_id,
				messageId: entry.message_id,
				readAt: entry.read_at,
			});
		}
		this.loadedChannels.add(channelId);
	}

	isLoaded(channelId: string): boolean {
		return this.loadedChannels.has(channelId);
	}

	handleGatewayReady(): void {
		this.receiptsByChannel = new Map();
		this.readAtByReader = new Map();
		this.loadedChannels = new Set();
		this.fetchedChannels.clear();
		this.pendingBatch.clear();
		this.anchorsByChannel.clear();
	}
}

export default new ReadReceiptsStore();
